// What the login screens tell the user about the last attempt. A notice lives in
// AuthContext, so signing out an account without a Gastronaut profile does not
// wipe the explanation of why it happened.
export type AuthNotice =
  | { kind: 'invalid-credentials' }
  | { kind: 'network' }
  | { kind: 'popup-closed' }
  | { kind: 'popup-blocked' }
  | { kind: 'too-many-requests' }
  | { kind: 'user-disabled' }
  | { kind: 'provider-unavailable' }
  | { kind: 'persistence-unavailable' }
  | { kind: 'profile-missing'; provider: 'google' | 'password' | 'other'; email: string | null }
  | { kind: 'profile-unavailable' }
  | { kind: 'unknown' };

const KIND_BY_CODE: Record<string, AuthNotice['kind']> = {
  'auth/wrong-password': 'invalid-credentials',
  'auth/user-not-found': 'invalid-credentials',
  'auth/invalid-credential': 'invalid-credentials',
  'auth/invalid-login-credentials': 'invalid-credentials',
  'auth/invalid-email': 'invalid-credentials',
  'auth/network-request-failed': 'network',
  'auth/popup-closed-by-user': 'popup-closed',
  'auth/cancelled-popup-request': 'popup-closed',
  'auth/user-cancelled': 'popup-closed',
  'auth/popup-blocked': 'popup-blocked',
  'auth/too-many-requests': 'too-many-requests',
  'auth/user-disabled': 'user-disabled',
  'auth/unauthorized-domain': 'provider-unavailable',
  'auth/operation-not-allowed': 'provider-unavailable',
  'auth/web-storage-unsupported': 'persistence-unavailable',
};

export const authNoticeFor = (error: unknown): AuthNotice => {
  const code = (error as { code?: unknown } | undefined)?.code;
  const kind = typeof code === 'string' ? KIND_BY_CODE[code] : undefined;
  return { kind: kind ?? 'unknown' } as AuthNotice;
};

// English keys of the auth translation namespace.
export const authNoticeText = (notice: AuthNotice): string => {
  switch (notice.kind) {
    case 'invalid-credentials':
      return 'The email address or password is not correct.';
    case 'network':
      return 'Sign-in could not be completed because of a connection problem. Please try again.';
    case 'popup-closed':
      return 'The Google window was closed before the sign-in finished.';
    case 'popup-blocked':
      return 'The browser blocked the Google window. Please allow pop-ups for this page and try again.';
    case 'too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'user-disabled':
      return 'This account has been deactivated. Please contact your restaurant.';
    case 'provider-unavailable':
      return 'This sign-in method is not available here. Please sign in with email and password.';
    case 'persistence-unavailable':
      return 'This browser does not allow staying signed in. Untick "Stay signed in on this device" and try again.';
    case 'profile-missing':
      return notice.provider === 'google'
        ? 'Your Google account is not connected to a Gastronaut access yet. Choose another Google account or sign in with email and password.'
        : 'There is no Gastronaut access for this account. Please ask your restaurant for an invitation.';
    case 'profile-unavailable':
      return 'Your access could not be loaded because of a connection problem.';
    default:
      return 'Sign-in failed. Please try again.';
  }
};
